import { InfoNotice } from '@/components/patterns/info-notice'
import { Button } from '@/components/ui/button'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useCheckoutAttemptStatus } from '@/lib/services/checkout/checkout.query'
import { useNavigate, useParams } from '@tanstack/react-router'
import { Loader2, Lock } from 'lucide-react'
import { paymentFinalizationDefaults } from './payment-finalization'
import { getCartFinalizationAttemptId } from './pos-finalization-contract'
import { getOrderQuoteLockState } from '@/lib/order-quote'

function normalizeAttemptState(rawState) {
  const state = String(rawState ?? '')
    .toLowerCase()
    .trim()

  switch (state) {
    case 'submitted':
    case 'awaiting_customer_action':
      return 'awaiting_customer_action'
    case 'processing_payment':
    case 'payment_succeeded':
      return 'processing_payment'
    case 'completed':
      return 'completed'
    case 'payment_failed':
    case 'cancelled':
      return 'payment_failed'
    case 'expired':
      return 'expired'
    default:
      return null
  }
}

function lockPresentationFor(cart, attemptStatus) {
  const salesOrderId =
    Number(cart?.sales_order_id ?? 0) || Number(attemptStatus?.sales_order_id ?? 0) || null

  if (salesOrderId) {
    return {
      tone: 'warning',
      title: 'Cart Converted to Order',
      description: 'This cart has been converted to an order and can no longer be modified.',
      showSalesOrderAction: true,
      pending: false,
    }
  }

  const attemptState = normalizeAttemptState(
    attemptStatus?.state ??
      attemptStatus?.attempt_status ??
      cart?.active_order_placement_attempt?.status
  )
  const attemptMessage = String(attemptStatus?.message ?? '').trim()
  const orderQuoteLockState = getOrderQuoteLockState(cart)

  switch (attemptState) {
    case 'awaiting_customer_action':
      return {
        tone: 'info',
        title: 'Waiting for Payment Confirmation',
        // Pay Now picks the waiting payment up, so staff are never stuck here.
        description: `${attemptMessage || 'A card payment is waiting to be confirmed.'} Open Pay Now to check on it or cancel it.`,
        showSalesOrderAction: false,
        pending: true,
      }
    case 'processing_payment':
      return {
        tone: 'info',
        title: 'Finalizing Order',
        description:
          attemptMessage ||
          'This cart is locked while we finalize the order and record the payment.',
        showSalesOrderAction: false,
        pending: true,
      }
    case 'completed':
      return {
        tone: 'info',
        title: 'Finishing Order Sync',
        description:
          attemptMessage ||
          'Payment completed. We are linking the finalized order back to this cart.',
        showSalesOrderAction: false,
        pending: true,
      }
    case 'payment_failed':
    case 'expired':
      return {
        tone: 'warning',
        title: 'Refreshing Cart Status',
        description:
          attemptMessage ||
          'This cart is refreshing after a payment attempt. Please wait a moment.',
        showSalesOrderAction: false,
        pending: true,
      }
    default:
      if (orderQuoteLockState.canCompleteOrder) {
        return {
          tone: 'info',
          title: 'Payment Collected',
          description:
            'Complete this order using the payment already collected, or refund the payments below. No additional charge is needed.',
          showSalesOrderAction: false,
          pending: false,
        }
      }

      if (orderQuoteLockState.isPreparedPosPaymentSessionLock) {
        return {
          tone: 'warning',
          title: 'Payment Session Open',
          description:
            'Reopen the payment modal and cancel or complete the prepared card payment before changing this cart.',
          showSalesOrderAction: false,
          pending: false,
        }
      }

      return {
        tone: 'warning',
        title: 'Cart Locked',
        description: 'This cart is temporarily locked and cannot be modified.',
        showSalesOrderAction: false,
        pending: false,
      }
  }
}

export function PosCartLockBanner({ cart, onStartNewCart }) {
  const navigate = useNavigate()
  const pathHelper = usePathHelper()
  const { orgId } = useParams({ strict: false })
  const activeAttemptId = getCartFinalizationAttemptId(cart)
  const isCartLocked = getOrderQuoteLockState(cart).locked
  const { data: liveAttemptStatus } = useCheckoutAttemptStatus(activeAttemptId, {
    enabled: Boolean(isCartLocked && activeAttemptId),
    refetchInterval: paymentFinalizationDefaults.intervalMs,
  })

  if (!isCartLocked) {
    return null
  }

  const presentation = lockPresentationFor(cart, liveAttemptStatus)
  const tone = presentation.tone ?? 'warning'
  const salesOrderId =
    Number(cart?.sales_order_id ?? 0) || Number(liveAttemptStatus?.sales_order_id ?? 0) || null
  const navigationOrgId = String(cart?.organization_id ?? orgId ?? '').trim()

  return (
    <InfoNotice
      className="mb-4"
      title={presentation.title}
      tone={tone}
      icon={presentation.pending ? <Loader2 className="animate-spin" /> : <Lock />}
      action={
        presentation.showSalesOrderAction && salesOrderId && navigationOrgId ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              className="h-10"
              onClick={() =>
                navigate({
                  ...pathHelper.salesOrders.show(salesOrderId).using({ orgId: navigationOrgId })
                    .link,
                  ignoreBlocker: true,
                })
              }
              variant="secondary"
            >
              View Order
            </Button>
            {onStartNewCart ? (
              <Button
                className="h-10"
                variant="outline"
                onClick={() => onStartNewCart()}
                type="button"
              >
                Start New Cart
              </Button>
            ) : null}
          </div>
        ) : null
      }
    >
      {presentation.description}
    </InfoNotice>
  )
}
